import * as React from "react";
import { useState } from "react";
import Dialog from "@imminently/decisively-ui/Dialog";
import Button from "@imminently/decisively-ui/Button";
import Select from "@imminently/decisively-ui/Select";

import {
  ComponentPage,
  ComponentSection,
  ComponentPreview,
  ControlPanel,
  ControlRow,
} from "../components/ComponentPage";

// ─── Anatomy helpers ──────────────────────────────────────────────────────────

interface AnatomyRowProps {
  component: string;
  element: string;
  intent?: string;
  styles: string;
  notes?: string;
}

function AnatomyLabel({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <span
      className={`absolute -top-1.5 left-1.5 px-1 text-[10px] font-semibold text-blue-500 bg-popover leading-none ${className ?? ""}`}
    >
      {children}
    </span>
  );
}

function AnatomyRow({ component, element, intent, styles, notes }: AnatomyRowProps) {
  return (
    <tr className="border-b border-border last:border-0">
      <td className="py-2 p-4 align-top">
        <code className="text-xs bg-muted px-1.5 py-0.5 rounded font-mono">{component}</code>
      </td>
      <td className="py-2 pr-4 align-top text-xs text-muted-foreground font-mono">{element}</td>
      <td className="py-2 pr-4 align-top text-xs text-muted-foreground">{intent}</td>
      <td className="py-2 pr-4 align-top text-xs text-muted-foreground">{styles}</td>
      {notes && <td className="py-2 align-top text-xs text-muted-foreground">{notes}</td>}
    </tr>
  );
}

type DialogSize = "compact" | "default" | "wide" | "cover";

const sizeOptions = [
  { value: "compact", label: "Compact" },
  { value: "default", label: "Default" },
  { value: "wide", label: "Wide" },
  { value: "cover", label: "Cover" },
] as const;

const contentClassNameBySize: Record<Exclude<DialogSize, "cover">, string> = {
  compact: "w-full max-w-md",
  default: "w-full max-w-2xl",
  wide: "w-full max-w-5xl",
};

export function DialogPage() {
  const [open, setOpen] = useState(false);
  const [size, setSize] = useState<DialogSize>("default");

  const intent = size === "cover" ? "cover" : "form";
  const contentClassName = size === "cover" ? "p-0! flex flex-col" : contentClassNameBySize[size];

  return (
    <ComponentPage
      title="Dialog"
      description="Displays content that overlays the current page and requires interaction or acknowledgement."
    >
      <ComponentSection
        title="Playground"
        description="Open the dialog and change its size live from inside the modal."
      >
        <div className="grid gap-6 lg:grid-cols-2">
          <ComponentPreview>
            <Button
              onClick={() => setOpen(true)}
              label="Open dialog"
            >
              Open dialog
            </Button>
          </ComponentPreview>

          <ControlPanel>
            <ControlRow label="Initial size">
              <Select
                value={size}
                onValueChange={(value) => setSize((value ?? "default") as DialogSize)}
                options={sizeOptions.map((option) => ({ value: option.value, label: option.label }))}
              />
            </ControlRow>
          </ControlPanel>
        </div>

        <Dialog
          open={open}
          onOpenChange={setOpen}
          intent={intent}
        >
          <Dialog.Content className={contentClassName}>
            <Dialog.Header>
              <Dialog.Title>Resizable Dialog</Dialog.Title>
              <Dialog.Description>
                Use the size setting below to switch the dialog width while it is open.
              </Dialog.Description>
              <Dialog.Controls />
            </Dialog.Header>

            <Dialog.Body>
              <div className="grid gap-4">
                <div className="grid gap-1.5 max-w-xs">
                  <label
                    className="text-sm font-medium"
                    htmlFor="dialog-size-select"
                  >
                    Dialog Size
                  </label>
                  <Select
                    value={size}
                    onValueChange={(value) => setSize((value ?? "default") as DialogSize)}
                    options={sizeOptions.map((option) => ({ value: option.value, label: option.label }))}
                    placeholder="Select size"
                  />
                </div>

                <div className="grid gap-2">
                  <p className="text-sm text-muted-foreground">
                    Current size: <span className="font-medium text-foreground">{size}</span>
                  </p>
                  <div className="rounded-lg border border-border bg-muted/30 p-4">
                    <p className="text-sm text-muted-foreground">
                      This content stays the same while the dialog container changes size.
                    </p>
                  </div>
                </div>
              </div>
            </Dialog.Body>

            <Dialog.Actions>
              <Dialog.Actions.CancelButton label="Close">Close</Dialog.Actions.CancelButton>
              <Dialog.Actions.PrimaryButton
                onClick={() => setOpen(false)}
                label="Done"
              >
                Done
              </Dialog.Actions.PrimaryButton>
            </Dialog.Actions>
          </Dialog.Content>
        </Dialog>
      </ComponentSection>

      {/* ── Anatomy ─────────────────────────────────────────────────── */}
      <ComponentSection
        title="Anatomy"
        description="A breakdown of the internal structure, spacing, and styling tokens used by the Dialog."
      >
        {/* Annotated visual diagram — uses real Dialog sub-components */}
        <Dialog.InlineRoot intent="form">
          <Dialog.Content className="flex flex-col gap-2 w-full! max-w-full! relative outline-1 outline-dashed outline-blue-300 rounded-lg">
            {/* Content region outline */}
            <AnatomyLabel>Dialog.Content</AnatomyLabel>

            {/* Header region */}
            <Dialog.Header className="relative outline-1 outline-dashed outline-blue-300 rounded">
              <AnatomyLabel>Dialog.Header</AnatomyLabel>
              <Dialog.Title>Dialog Title</Dialog.Title>
              <Dialog.Description className="text-muted-foreground">
                A short description that gives context about the dialog's purpose.
              </Dialog.Description>
            </Dialog.Header>
            <Dialog.Controls className="outline-1 outline-dashed outline-blue-300 rounded" />

            {/* Body region */}
            <Dialog.Body className="relative outline-1 outline-dashed outline-blue-300 rounded">
              <AnatomyLabel>Dialog.Body</AnatomyLabel>
              <p>Main content area — forms, information, or any custom React content.</p>
            </Dialog.Body>

            {/* Actions region */}
            <Dialog.Actions className="relative outline-1 outline-dashed outline-blue-300 rounded">
              <AnatomyLabel>Dialog.Actions</AnatomyLabel>
              <Dialog.Actions.CancelButton label="Cancel">Cancel</Dialog.Actions.CancelButton>
              <Dialog.Actions.PrimaryButton label="Confirm">Confirm</Dialog.Actions.PrimaryButton>
            </Dialog.Actions>
          </Dialog.Content>
        </Dialog.InlineRoot>

        {/* Component table */}
        <div className="mt-8 overflow-x-auto rounded-lg border border-border">
          <table className="w-full min-w-160 text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40">
                <th className="py-2 px-4 text-left text-xs font-semibold text-foreground">Component</th>
                <th className="py-2 px-4 text-left text-xs font-semibold text-foreground">Element</th>
                <th className="py-2 px-4 text-left text-xs font-semibold text-foreground">Intent</th>
                <th className="py-2 px-4 text-left text-xs font-semibold text-foreground">Key styles</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              <AnatomyRow
                component="Dialog"
                element="(context provider)"
                intent="Root component. Manages open state and mounts the modal portal with a backdrop overlay."
                styles="Sets DialogIntent context (compact | form | cover). Wraps Radix Modal with portal + overlay."
              />
              <AnatomyRow
                component="Dialog.InlineRoot"
                element="(context provider)"
                intent="Context-only root for static or inline usage — no overlay or portal. Useful for documentation, previews, and tests."
                styles="Sets DialogIntent + Radix contexts without any portal or overlay. Use for inline previews and documentation."
              />
              <AnatomyRow
                component="Dialog.Content"
                element="<div> (inline) · Radix DialogPrimitive.Content (modal)"
                intent="The visible dialog surface. Provides the background, border, shadow, and padding that frames all other sub-components."
                styles="padding: 1rem · bg: var(--popover) · color: var(--popover-foreground) · border: 1px solid var(--border) · border-radius: var(--radius-lg) · box-shadow: 0 10px 25px …"
              />
              <AnatomyRow
                component="Dialog.Content[data-intent=compact]"
                element=""
                intent="Narrow dialogs — alerts, confirmations, and simple prompts."
                styles="width: min(90vw, 32rem)"
              />
              <AnatomyRow
                component="Dialog.Content[data-intent=form]"
                element=""
                intent="Standard dialogs — forms, detail views, and multi-field workflows."
                styles="width: min(90vw, 56rem) · min-width: 520px (mobile: 90vw)"
              />
              <AnatomyRow
                component="Dialog.Content[data-intent=cover]"
                element=""
                intent="Full-viewport dialogs — complex editors, multi-step wizards, and immersive content."
                styles="width: 90vw · height: 90vh · max-height: 90vh"
              />
              <AnatomyRow
                component="Dialog.Header"
                element="<div>"
                intent="Top section of the dialog. Groups the title, description, and controls into a single header block."
                styles="display: flex · flex-direction: column · gap: 0.25rem"
              />
              <AnatomyRow
                component="Dialog.Title"
                element="Radix DialogPrimitive.Title"
                intent="Primary heading of the dialog. Communicates what the dialog is for and satisfies the Radix accessible name requirement."
                styles="font-size: 1.5rem · font-weight: 700 · line-height: 1.2 · padding-right: 6rem · color: var(--foreground) · font-family: Montserrat / Nunito"
              />
              <AnatomyRow
                component="Dialog.Description"
                element="Radix DialogPrimitive.Description"
                intent="Optional supporting text beneath the title. Provides context, instructions, or warnings about the action."
                styles="font-size: 0.875rem · line-height: 1.25"
              />
              <AnatomyRow
                component="Dialog.Controls"
                element="Modal.Actions · <div>"
                intent="Utility controls pinned to the top-right of the dialog. Typically renders a close button and an optional expand/collapse toggle."
                styles="position: absolute · top: 0 · right: 0 · display: flex · gap: 0.5rem. Place inside any position: relative ancestor (typically Dialog.Content). Renders resize and/or close icon buttons."
              />
              <AnatomyRow
                component="Dialog.Body"
                element="<div> (or asChild slot)"
                intent="Main content area. Contains forms, informational content, or any custom React content."
                styles="display: flex · flex-direction: column · gap: 1rem · padding-top: 1rem · padding-bottom: 1rem"
              />
              <AnatomyRow
                component="Dialog.Actions"
                element="<div>"
                intent="Footer action bar. Used for form submission, confirmation, and dismissal — primary action on the right, secondary on the left."
                styles="display: flex · justify-content: flex-end · gap: 0.5rem · padding-top: 0.5rem"
              />
              <AnatomyRow
                component="Dialog.Actions.PrimaryButton"
                element="Button"
                intent="Confirms or submits the dialog's primary action (e.g. Save, Confirm, Submit)."
                styles="intent=primary · inherits all Button sizing/variant styles"
              />
              <AnatomyRow
                component="Dialog.Actions.CancelButton"
                element="Button"
                intent="Dismisses or cancels through the guarded dialog close path."
                styles="default Button variant (no explicit intent override)"
              />
            </tbody>
          </table>
        </div>
      </ComponentSection>
    </ComponentPage>
  );
}
